import {
  ChevronLeft,
  DoNotDisturbOn,
  EditOutlined,
  ExpandMore,
  NoteAltOutlined,
  SaveOutlined,
  Close as CloseIcon,
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  Link as LinkMui,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
  Tooltip,
  Snackbar,
} from '@mui/material'
import { Link as LinkRouter, useLocation, useNavigate, useParams } from 'react-router-dom'
import { DatePicker, Expandable, getRepCodeName, NumberField, OpsOwner } from '../helper'
import { rmaMerchandizeConditions, rmaStatuses } from '../lists'
import { useEffect, useState, useContext } from 'react'
import { rmaType, rmaLineType, salesOrderLineType, invoiceType } from '../types'
import './RmaDetails.css'
import axios from 'axios'
import dayjs from 'dayjs'
import { dataContext } from '..'
import { RmaEmailToCustomer, RmaEmailToVendor, RmaInternalEscalationEmail } from './RmaEmails'
import SalesOrderNotes from '../SalesOrder/SalesOrderNotes'
import ErrorScreen from '../Misc/ErrorScreen'

export type rmaDetailType = rmaType & {
  so_number: string
  customer_po: string
  job: string
  customer_id: string
  customer_parent_name: string
  last_update_user: string
  original_sales_rep: string
  government_award_number: string
}

const stepToIndex = new Map([
  [1, 1],
  [2, 1],
  [3, 2],
  [4, 6],
  [5, 3],
  [6, 4],
  [7, 5],
  [8, 6],
  [9, 6],
  [10, 6],
])

function DeniedStepIcon(props: { loading: boolean; rma: rmaType }) {
  return props.loading
    ? () => <Skeleton variant="circular" width={24} height={24} />
    : props.rma.rma_vendor_action_request_result === 'D'
    ? () => <DoNotDisturbOn color="primary" className="rma-stepper-denied-icon" />
    : undefined
}

function RmaDetails() {
  const { id } = useParams()
  const { user, salesReps } = useContext(dataContext)

  const navigate = useNavigate()
  const location = useLocation()

  const [editDetails, setEditDetails] = useState(id === 'create')
  const [editEmail, setEditEmail] = useState(false)
  const [rma, setRma] = useState<rmaDetailType>({} as rmaDetailType)
  const [rmaCopy, setRmaCopy] = useState<rmaDetailType>({} as rmaDetailType)
  const [rmaLines, setRmaLines] = useState<(rmaLineType & salesOrderLineType)[]>([
    {} as rmaLineType & salesOrderLineType,
  ])
  const [relatedInvoices, setRelatedInvoices] = useState<invoiceType[]>([{} as invoiceType])
  const [step, setStep] = useState(0)
  const [selectedEmail, setSelectedEmail] = useState(0)
  const [stickyHeader, setStickyHeader] = useState(true)
  const [selectedLines, setSelectedLines] = useState<(rmaLineType & salesOrderLineType)[]>([])
  const [deleteRmaDialogOpen, setDeleteRmaDialogOpen] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const [loading, setLoading] = useState(true)
  const [editError, setEditError] = useState(false)
  const [emailSaveError, setEmailSaveError] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [opsOwnerError, setOpsOwnerError] = useState(false)
  const [editRmaLines, setEditRmaLines] = useState(false)
  const [rmaLinesCopy, setRmaLinesCopy] = useState<(rmaLineType & salesOrderLineType)[]>([])

  useEffect(() => {
    if (!user.active_flag) {
      return
    }

    if (id === 'create') {
      if (!location.state) {
        navigate('/salesorder')
        return
      }
      setRma({
        sales_order_txn_id: location.state.salesOrder.sales_order_txn_id,
        so_number: location.state.salesOrder.ref_number,
        customer_po: location.state.salesOrder.customer_po_number,
        original_sales_rep: location.state.salesOrder.rep_code,
        customer_parent_name: location.state.salesOrder.parent_name,
        rma_initiation_date: new Date(),
        rma_status_id: 1,
        rma_vendor_action_request:
          'Dear Vendor,\n' +
          'Our customer would like to return items on this order. ' +
          'Below you will find a list of products needing to be returned, ' +
          'as well as their corresponding service tags/serial numbers, ' +
          'reason for their return and the condition that they are in.  ' +
          'Please begin the RMA process.\n' +
          'Thank you',
        operations_owner: location.state.salesOrder.operations_owner,
      } as rmaDetailType)
      setRmaLines(
        location.state.lineItems
          .sort(
            (a: salesOrderLineType, b: salesOrderLineType) =>
              (a.line_numerical_order ?? 0) - (b.line_numerical_order ?? 0),
          )
          .map((line: salesOrderLineType) => ({
            sales_order_line_txn_id: line.sales_order_line_txn_id,
            sales_order_txn_id: location.state.salesOrder.sales_order_txn_id,
            item_list_id: line.item_list_id,
            item_name: line.item_name,
            item_desc: line.item_desc,
            rma_quantity: line.quantity,
            serial_number: null,
            rma_comment: null,
          })),
      )
      setLoading(false)
    } else {
      axios
        .get(`/rma/${id}?${user.user_type === 'Sales Rep' ? `salesUser=${user.user_id}` : ''}`)
        .then(res => {
          setRma(res.data.rma)
          setRmaCopy(res.data.rma)
          setStep(stepToIndex.get(res.data.rma.rma_status_id) || 0)
          setRmaLines(res.data.rmaLines)
          setRmaLinesCopy(res.data.rmaLines)
          setRelatedInvoices(res.data.relatedInvoices)
          setLoadError(false)
        })
        .catch(err => {
          console.error(err)
          setLoadError(true)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [user])

  useEffect(() => {
    document.title = `RMA ${rma.rma_number ? `| ${rma.rma_number}` : ''} | Salestrack`
  }, [rma])

  const handleSubmitRma = () => {
    axios
      .post(`/createRma`, {
        rma: rma,
        lineItems: rmaLines,
        user: user.user_id,
        userEmail: user.email,
        soOwner: location.state.salesOrder.operations_owner_email,
      })
      .then(res => {
        setSubmitError(false)
        navigate(`/rma/${res.data.rmaId}`)
        window.location.reload()
      })
      .catch(err => {
        setSubmitError(true)
        console.log(err)
      })
  }

  const handleEditRma = () => {
    const editRma = {
      rma_initiation_date: rma.rma_initiation_date,
      vendor_name: rma.vendor_name,
      rma_merchandize_condition_id: rma.rma_merchandize_condition_id,
      customer_name: rma.customer_name,
      customer_phone: rma.customer_phone,
      customer_email: rma.customer_email,
      vendor_number: rma.vendor_number,
      rma_customer_reason: rma.rma_customer_reason,
      pickup_contact: rma.pickup_contact,
      pickup_phone_number: rma.pickup_phone_number,
      pickup_email_id: rma.pickup_email_id,
      pickup_address: rma.pickup_address,
      rma_status_id: rma.rma_status_id,
      rep_code: rma.rep_code,
      tracking_details_customer_to_vendor: rma.tracking_details_customer_to_vendor,
      special_instructions: rma.special_instructions,
    }
    axios
      .post(`/rma/${id}/edit`, { rma: editRma, user: user.user_id })
      .then(() => {
        setEditDetails(false)
        const newRma = { ...rma, last_update_date: new Date(), last_update_user: user.display_name ?? '' }
        setRma(newRma)
        setRmaCopy(newRma)
        setStep(stepToIndex.get(newRma.rma_status_id) || 0)
        setEditError(false)
      })
      .catch(err => {
        console.error(err)
        setEditError(true)
      })
  }

  const handleSaveEmail = () => {
    if (selectedEmail === 0) {
      axios
        .post(`/rma/${id}/edit`, {
          rma: {
            rma_vendor_action_request_email_date: rma.rma_vendor_action_request_email_date,
            rma_vendor_action_request: rma.rma_vendor_action_request,
            rma_vendor_action_request_result: rma.rma_vendor_action_request_result,
            vendor_rma_number: rma.vendor_rma_number,
            rma_expiration_date: rma.rma_expiration_date,
            vendor_poc_name: rma.vendor_poc_name,
            vendor_poc_phone: rma.vendor_poc_phone,
            restocking_fee: rma.restocking_fee,
            rma_vendor_action_request_result_receive_date: rma.rma_vendor_action_request_result_receive_date,
            vendor_poc_email: rma.vendor_poc_email,
          },
          user: user.user_id,
        })
        .then(() => {
          setEditEmail(false)
          const newRma = { ...rma, last_update_date: new Date(), last_update_user: user.display_name ?? '' }
          setRma(newRma)
          setRmaCopy(newRma)
          setEmailSaveError(false)
        })
        .catch(err => {
          console.error(err)
          setEmailSaveError(true)
        })
    } else if (selectedEmail === 1) {
      axios
        .post(`/rma/${id}/edit`, {
          rma: {
            rma_escalation_action_result: rma.rma_escalation_action_result,
            rma_escalation_action_user_id: rma.rma_escalation_action_user_id,
            rma_escalation_date: rma.rma_escalation_date,
            rma_escalation_action_result_date: rma.rma_escalation_action_result_date,
          },
          user: user.user_id,
        })
        .then(() => {
          setEditEmail(false)
          const newRma = { ...rma, last_update_date: new Date(), last_update_user: user.display_name ?? '' }
          setRma(newRma)
          setRmaCopy(newRma)
          setEmailSaveError(false)
        })
        .catch(err => {
          console.error(err)
          setEmailSaveError(true)
        })
    } else if (selectedEmail === 2) {
      axios
        .post(`/rma/${id}/edit`, {
          rma: {
            customer_instruction: rma.customer_instruction,
            rma_customer_email_date: rma.rma_customer_email_date,
            tracking_details_vendor_to_customer: rma.tracking_details_vendor_to_customer,
          },
          user: user.user_id,
        })
        .then(() => {
          setEditEmail(false)
          const newRma = { ...rma, last_update_date: new Date(), last_update_user: user.display_name ?? '' }
          setRma(newRma)
          setRmaCopy(newRma)
          setEmailSaveError(false)
        })
        .catch(err => {
          console.error(err)
          setEmailSaveError(true)
        })
    }
  }

  const handleSaveRmaLines = () => {
    // Check if any changes were made by comparing with the copy
    const hasChanges = rmaLines.some((line, index) => {
      const originalLine = rmaLinesCopy[index]
      return (
        line.rma_quantity !== originalLine.rma_quantity ||
        line.serial_number !== originalLine.serial_number ||
        line.rma_comment !== originalLine.rma_comment
      )
    })

    // If no changes were made, just exit edit mode without sending POST
    if (!hasChanges) {
      setEditRmaLines(false)
      return
    }

    // Send the rmaLines data to the backend
    const requestData = {
      rma: {},
      rmaLines: rmaLines,
      user: user.user_id,
    }

    axios
      .post(`/rma/${id}/edit`, requestData)
      .then(() => {
        setEditRmaLines(false)
        setRmaLinesCopy(rmaLines)
        setEditError(false)
      })
      .catch(err => {
        console.error(err)
        setEditError(true)
      })
  }

  const handleCancelRmaLines = () => {
    setEditRmaLines(false)
    setRmaLines(rmaLinesCopy)
  }

  const handleOpsOwner = (rmaId: string, owner: string | null) => {
    axios
      .post(`/rma/${rmaId}/edit`, { id: rmaId, rma: { operations_owner: owner }, user: user.user_id })
      .then(() => {
        setRma(prev => ({ ...prev, operations_owner: owner }))
        setOpsOwnerError(false)
      })
      .catch(error => {
        setOpsOwnerError(true)
        console.error(error)
      })
  }

  if (loadError) {
    return (
      <div className="rma-details rma-details-error-container">
        <ErrorScreen
          title="RMA Load Failed"
          errorText="Failed to load RMA details. Please try again or contact support if the error persists."
        />
      </div>
    )
  }

  return (
    <div className="rma-details">
      <div className="rma-details-header">
        <div className="rma-details-header-left">
          <Button
            variant="contained"
            size="large"
            startIcon={<ChevronLeft />}
            onClick={() => {
              if (id === 'create') {
                navigate(`/salesorder/${rma.sales_order_txn_id}`)
              } else {
                navigate('/rma')
              }
            }}
            loading={loading}
          >
            Return {id === 'create' ? '' : 'to List'}
          </Button>
          <div className="rma-details-header-chips">
            {loading ? <Skeleton width={120} height={32} /> : <Chip label={`Case #${rma.rma_number}`} />}
            {id !== 'create' &&
              (loading ? (
                <Skeleton width={180} height={32} />
              ) : (
                <>
                  <OpsOwner
                    operationsOwner={rma.operations_owner}
                    objectId={rma.rma_id}
                    handleEditOpsOwner={handleOpsOwner}
                  />
                  <Snackbar open={opsOwnerError} onClose={() => setOpsOwnerError(false)}>
                    <Alert severity="error">
                      <AlertTitle>Error</AlertTitle>
                      Failed to update operations owner. Please try again or contact support if the error persists.
                    </Alert>
                  </Snackbar>
                </>
              ))}
          </div>
        </div>
        {id === 'create' && (
          <Button
            variant="contained"
            size="large"
            disabled={
              !(
                rma.vendor_number &&
                rma.rma_status_id &&
                rma.vendor_name &&
                rma.rma_customer_reason &&
                rma.rep_code &&
                rma.rma_merchandize_condition_id &&
                rma.customer_name &&
                rma.customer_phone &&
                rma.customer_email &&
                rma.pickup_contact &&
                rma.pickup_phone_number &&
                rma.pickup_email_id &&
                rma.pickup_address
              )
            }
            onClick={handleSubmitRma}
            loading={loading}
          >
            Submit RMA
          </Button>
        )}
        <Snackbar open={submitError} onClose={() => setSubmitError(false)}>
          <Alert severity="error">
            <AlertTitle>RMA Submission Failed</AlertTitle>
            An error occurred while submitting the RMA. Please try again or contact support if the error persists.
          </Alert>
        </Snackbar>
        {id !== 'create' && (
          <>
            {loading ? (
              <Skeleton width={200} height={24} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Last Update: {dayjs(rma.last_update_date).format('MM/DD/YYYY')} by {rma.last_update_user}
              </Typography>
            )}
            <Button
              variant="contained"
              size="large"
              startIcon={<NoteAltOutlined />}
              onClick={() => setNotesOpen(true)}
              loading={loading}
            >
              Notes
            </Button>
            <Drawer anchor="right" open={notesOpen} onClose={() => setNotesOpen(false)}>
              <SalesOrderNotes
                salesOrderId={rma.rma_id}
                objectType="rma"
                setNotesOpen={setNotesOpen}
                noteDraft={noteDraft}
                setNoteDraft={setNoteDraft}
              />
            </Drawer>
          </>
        )}
      </div>
      <Divider />
      <div className="rma-details-body">
        {id === 'create' && <div> {/* for header padding */} </div>}
        {id !== 'create' && (
          <Stepper alternativeLabel activeStep={step}>
            <Step>
              <StepLabel
                slots={{
                  stepIcon: loading ? () => <Skeleton variant="circular" width={24} height={24} /> : undefined,
                }}
              >
                {loading ? <Skeleton width={120} /> : 'Initiated'}
              </StepLabel>
            </Step>
            <Step>
              <StepLabel
                slots={{
                  stepIcon: loading ? () => <Skeleton variant="circular" width={24} height={24} /> : undefined,
                }}
              >
                {loading ? (
                  <Skeleton width={120} />
                ) : (
                  <>
                    Vendor Response <br />
                    {(rma.rma_vendor_action_request_result === 'D' ||
                      (!rma.rma_vendor_action_request_result && rma.rma_status_id === 4)) && (
                      <Typography variant="caption" color="text.secondary">
                        Rejected
                      </Typography>
                    )}
                    {(rma.rma_vendor_action_request_result === 'A' ||
                      (!rma.rma_vendor_action_request_result && [3, 5, 6, 7, 8, 10].includes(rma.rma_status_id))) && (
                      <Typography variant="caption" color="text.secondary">
                        Approved
                      </Typography>
                    )}
                  </>
                )}
              </StepLabel>
            </Step>
            <Step>
              <StepLabel
                slots={{
                  stepIcon: DeniedStepIcon({ loading, rma }),
                }}
              >
                {loading ? <Skeleton width={120} /> : 'Product Return'}
              </StepLabel>
            </Step>
            <Step>
              <StepLabel
                slots={{
                  stepIcon: DeniedStepIcon({ loading, rma }),
                }}
              >
                {loading ? <Skeleton width={120} /> : 'Replacement Product Ordered'}
              </StepLabel>
            </Step>
            <Step>
              <StepLabel
                slots={{
                  stepIcon: DeniedStepIcon({ loading, rma }),
                }}
              >
                {loading ? <Skeleton width={120} /> : 'Credit Issued'}
              </StepLabel>
            </Step>
            <Step>
              <StepLabel
                slots={{
                  stepIcon: DeniedStepIcon({ loading, rma }),
                }}
              >
                {loading ? <Skeleton width={120} /> : 'RMA Completed'}
              </StepLabel>
            </Step>
          </Stepper>
        )}
        <Container maxWidth={false} className="rma-details-info">
          <Container maxWidth={false} className="rma-details-info-header">
            <div className="rma-details-info-header-box">
              {loading ? (
                <>
                  <div className="rma-details-info-header-loading">
                    <Skeleton width={100} />
                    <Skeleton width={120} />
                  </div>
                  <div className="rma-details-info-header-loading">
                    <Skeleton width={100} />
                    <Skeleton width={120} />
                  </div>
                  <div className="rma-details-info-header-loading">
                    <Skeleton width={120} />
                    <Skeleton width={100} />
                  </div>
                  <div className="rma-details-info-header-loading">
                    <Skeleton width={100} />
                    <Skeleton width={150} />
                  </div>
                </>
              ) : (
                <>
                  <div className="rma-details-info-header-loading">
                    <Typography variant="body2" color="text.secondary">
                      Sales Order No:
                    </Typography>
                    <Typography variant="body1">{rma.so_number}</Typography>
                  </div>
                  <div className="rma-details-info-header-loading">
                    <Typography variant="body2" color="text.secondary">
                      Customer PO:
                    </Typography>
                    <Typography variant="body1">{rma.customer_po}</Typography>
                  </div>
                  <div className="rma-details-info-header-loading">
                    <Typography variant="body2" color="text.secondary">
                      Original Sales Rep:
                    </Typography>
                    <Typography variant="body1">
                      {`${rma.original_sales_rep} ${getRepCodeName(
                        salesReps.find(rep => rep.initial === rma.original_sales_rep),
                      )}`}
                    </Typography>
                  </div>
                  <div className="rma-details-info-header-loading">
                    <Typography variant="body2" color="text.secondary">
                      Customer Name:
                    </Typography>
                    <Typography variant="body1">{rma.customer_parent_name}</Typography>
                  </div>
                </>
              )}
            </div>
            {id !== 'create' &&
              user.user_type !== 'Office Staff' &&
              user.user_type !== 'Sales Rep' &&
              (editDetails ? (
                <>
                  <Button
                    size="large"
                    onClick={() => {
                      setEditDetails(false)
                      setRma(rmaCopy)
                    }}
                    loading={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="large"
                    variant="outlined"
                    startIcon={<SaveOutlined />}
                    onClick={handleEditRma}
                    loading={loading}
                  >
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button
                  size="large"
                  startIcon={<EditOutlined />}
                  onClick={() => setEditDetails(true)}
                  loading={loading}
                >
                  Edit
                </Button>
              ))}
          </Container>
          <div className="rma-error-alerts-container">
            {editError && (
              <Alert
                severity="error"
                action={
                  <>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => {
                        setEditError(false)
                        handleEditRma()
                      }}
                    >
                      RETRY
                    </Button>
                    <IconButton color="inherit" size="small" onClick={() => setEditError(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                }
              >
                <AlertTitle>Save Failed</AlertTitle>
                An error occurred while saving the changes. Please try again or contact support if the error persists.
              </Alert>
            )}
            {emailSaveError && (
              <Alert
                severity="error"
                action={
                  <>
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setEmailSaveError(false)
                        handleSaveEmail()
                      }}
                    >
                      RETRY
                    </Button>
                    <IconButton color="inherit" size="small" onClick={() => setEmailSaveError(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                }
              >
                <AlertTitle>Save Failed</AlertTitle>
                An error occurred while saving the email changes. Please try again or contact support if the error
                persists.
              </Alert>
            )}
          </div>
          <Grid container spacing="24px">
            <Grid size={4} className="rma-details-info-grid-item">
              {loading ? (
                <>
                  <Skeleton width={150} />
                  <Skeleton variant="rounded" width="100%" height={40} />
                </>
              ) : (
                <>
                  <div className="rma-details-info-grid-item-label">
                    <Typography variant="body2">RMA Initiation Date:</Typography>
                    <Typography variant="body2" color="error">
                      *
                    </Typography>
                  </div>
                  <DatePicker
                    size="small"
                    className="rma-details-info-grid-item-textfield"
                    disabled={!editDetails}
                    value={dayjs(rma.rma_initiation_date)}
                    onChange={value => {
                      setRma(prev => ({ ...prev, rma_initiation_date: value?.toDate() ?? prev.rma_initiation_date }))
                    }}
                  />
                </>
              )}
            </Grid>
            <Grid size={4} className="rma-details-info-grid-item">
              {loading ? (
                <>
                  <Skeleton width={100} />
                  <Skeleton variant="rounded" width="100%" height={40} />
                </>
              ) : (
                <>
                  <div className="rma-details-info-grid-item-label">
                    <Typography variant="body2">IMPRES PO:</Typography>
                    <Typography variant="body2" color="error">
                      *
                    </Typography>
                  </div>
                  <TextField
                    className="rma-details-info-grid-item-textfield"
                    size="small"
                    fullWidth
                    disabled={!editDetails}
                    value={rma.vendor_number ?? ''}
                    onChange={e => {
                      setRma(prev => ({ ...prev, vendor_number: e.target.value }))
                    }}
                  />
                </>
              )}
            </Grid>
            <Grid size={4} className="rma-details-info-grid-item">
              {loading ? (
                <>
                  <Skeleton width={100} />
                  <Skeleton variant="rounded" width="100%" height={40} />
                </>
              ) : (
                <>
                  <div className="rma-details-info-grid-item-label">
                    <Typography variant="body2">RMA Status:</Typography>
                    <Typography variant="body2" color="error">
                      *
                    </Typography>
                  </div>
                  <TextField
                    className="rma-details-info-grid-item-textfield"
                    size="small"
                    fullWidth
                    select
                    disabled={!editDetails}
                    value={rma.rma_status_id}
                    onChange={e => {
                      setRma(prev => ({ ...prev, rma_status_id: parseInt(e.target.value) }))
                    }}
                  >
                    {rmaStatuses.map(status => (
                      <MenuItem key={status.id} value={status.id}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}
            </Grid>
            <Grid size={4} className="rma-details-info-grid-item">
              {loading ? (
                <>
                  <Skeleton width={100} />
                  <Skeleton variant="rounded" width="100%" height={40} />
                </>
              ) : (
                <>
                  <div className="rma-details-info-grid-item-label">
                    <Typography variant="body2">Vendor Name:</Typography>
                    <Typography variant="body2" color="error">
                      *
                    </Typography>
                  </div>
                  <TextField
                    className="rma-details-info-grid-item-textfield"
                    size="small"
                    fullWidth
                    disabled={!editDetails}
                    value={rma.vendor_name ?? ''}
                    onChange={e => {
                      setRma(prev => ({ ...prev, vendor_name: e.target.value }))
                    }}
                  />
                </>
              )}
            </Grid>
            <Grid size={4} className="rma-details-info-grid-item">
              {loading ? (
                <>
                  <Skeleton width={150} />
                  <Skeleton variant="rounded" width="100%" height={40} />
                </>
              ) : (
                <>
                  <div className="rma-details-info-grid-item-label">
                    <Typography variant="body2">RMA Customer Reason:</Typography>
                    <Typography variant="body2" color="error">
                      *
                    </Typography>
                  </div>
                  <TextField
                    className="rma-details-info-grid-item-textfield"
                    size="small"
                    fullWidth
                    disabled={!editDetails}
                    value={rma.rma_customer_reason ?? ''}
                    onChange={e => {
                      setRma(prev => ({ ...prev, rma_customer_reason: e.target.value }))
                    }}
                  />
                </>
              )}
            </Grid>
            <Grid size={4} className="rma-details-info-grid-item">
              {loading ? (
                <>
                  <Skeleton width={100} />
                  <Skeleton variant="rounded" width="100%" height={40} />
                </>
              ) : (
                <>
                  <div className="rma-details-info-grid-item-label">
                    <Typography variant="body2">Created By:</Typography>
                    <Typography variant="body2" color="error">
                      *
                    </Typography>
                  </div>
                  <Autocomplete
                    className="rma-details-info-grid-item-textfield"
                    size="small"
                    fullWidth
                    options={salesReps}
                    value={salesReps.find(rep => rep.initial === rma.rep_code) ?? null}
                    getOptionLabel={option =>
                      `${option.initial} ${[option.inside_sales_rep, option.account_executive, option.engineer]
                        .filter(email => email !== null)
                        .join(', ')}`
                    }
                    renderInput={params => <TextField {...params} />}
                    disabled={!editDetails}
                    onChange={(_, value) => {
                      setRma(prev => ({ ...prev, rep_code: value?.initial ?? '' }))
                    }}
                  />
                </>
              )}
            </Grid>
            <Grid size={4}>
              {loading ? (
                <Stack spacing="24px">
                  <div className="rma-details-info-grid-item">
                    <Skeleton width={200} />
                    <Skeleton variant="rounded" width="100%" height={40} />
                  </div>
                  <div className="rma-details-info-grid-item">
                    <Skeleton width={150} />
                    <Skeleton variant="rounded" width="100%" height={40} />
                  </div>
                </Stack>
              ) : (
                <Stack spacing="24px">
                  <div className="rma-details-info-grid-item">
                    <div className="rma-details-info-grid-item-label">
                      <Typography variant="body2">RMA Merchandise Condition:</Typography>
                      <Typography variant="body2" color="error">
                        *
                      </Typography>
                    </div>
                    <TextField
                      className="rma-details-info-grid-item-textfield"
                      size="small"
                      fullWidth
                      select
                      disabled={!editDetails}
                      value={rma.rma_merchandize_condition_id}
                      onChange={e => {
                        setRma(prev => ({ ...prev, rma_merchandize_condition_id: parseInt(e.target.value) }))
                      }}
                    >
                      {rmaMerchandizeConditions.map(condition => (
                        <MenuItem key={condition.id} value={condition.id}>
                          {condition.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                  <div className="rma-details-info-grid-item">
                    <div className="rma-details-info-grid-item-label">
                      <Typography variant="body2">Customer Contact Name:</Typography>
                      <Typography variant="body2" color="error">
                        *
                      </Typography>
                    </div>
                    <TextField
                      className="rma-details-info-grid-item-textfield"
                      size="small"
                      fullWidth
                      disabled={!editDetails}
                      value={rma.customer_name ?? ''}
                      onChange={e => {
                        setRma(prev => ({ ...prev, customer_name: e.target.value }))
                      }}
                    />
                  </div>
                </Stack>
              )}
            </Grid>
            <Grid size={4}>
              {loading ? (
                <Stack spacing="24px">
                  <div className="rma-details-info-grid-item">
                    <Skeleton width={150} />
                    <Skeleton variant="rounded" width="100%" height={40} />
                  </div>
                  <div className="rma-details-info-grid-item">
                    <Skeleton width={170} />
                    <Skeleton variant="rounded" width="100%" height={40} />
                  </div>
                </Stack>
              ) : (
                <Stack spacing="24px">
                  <div className="rma-details-info-grid-item">
                    <div className="rma-details-info-grid-item-label">
                      <Typography variant="body2">Pickup Contact Name:</Typography>
                      <Typography variant="body2" color="error">
                        *
                      </Typography>
                    </div>
                    <TextField
                      className="rma-details-info-grid-item-textfield"
                      size="small"
                      fullWidth
                      disabled={!editDetails}
                      value={rma.pickup_contact ?? ''}
                      onChange={e => {
                        setRma(prev => ({ ...prev, pickup_contact: e.target.value }))
                      }}
                    />
                  </div>
                  <div className="rma-details-info-grid-item">
                    <div className="rma-details-info-grid-item-label">
                      <Typography variant="body2">Pickup Contact Phone:</Typography>
                      <Typography variant="body2" color="error">
                        *
                      </Typography>
                    </div>
                    <TextField
                      className="rma-details-info-grid-item-textfield"
                      size="small"
                      fullWidth
                      disabled={!editDetails}
                      value={rma.pickup_phone_number ?? ''}
                      onChange={e => {
                        setRma(prev => ({ ...prev, pickup_phone_number: e.target.value }))
                      }}
                    />
                  </div>
                </Stack>
              )}
            </Grid>
            <Grid size={4} className="rma-details-info-grid-item">
              {loading ? (
                <>
                  <Skeleton width={250} />
                  <Skeleton variant="rounded" width="100%" height={120} />
                </>
              ) : (
                <>
                  <div className="rma-details-info-grid-item-label">
                    <Typography variant="body2">Tracking Details Customer to Vendor:</Typography>
                  </div>
                  <TextField
                    className="rma-details-info-grid-item-textfield"
                    size="small"
                    fullWidth
                    multiline
                    rows={5.4}
                    disabled={!editDetails}
                    value={rma.tracking_details_customer_to_vendor}
                    onChange={e => {
                      setRma(prev => ({ ...prev, tracking_details_customer_to_vendor: e.target.value }))
                    }}
                  />
                </>
              )}
            </Grid>
            <Grid size={4}>
              {loading ? (
                <Stack spacing="24px">
                  <div className="rma-details-info-grid-item">
                    <Skeleton width={150} />
                    <Skeleton variant="rounded" width="100%" height={40} />
                  </div>
                  <div className="rma-details-info-grid-item">
                    <Skeleton width={160} />
                    <Skeleton variant="rounded" width="100%" height={40} />
                  </div>
                </Stack>
              ) : (
                <Stack spacing="24px">
                  <div className="rma-details-info-grid-item">
                    <div className="rma-details-info-grid-item-label">
                      <Typography variant="body2">Customer Contact Phone:</Typography>
                      <Typography variant="body2" color="error">
                        *
                      </Typography>
                    </div>
                    <TextField
                      className="rma-details-info-grid-item-textfield"
                      size="small"
                      fullWidth
                      disabled={!editDetails}
                      value={rma.customer_phone ?? ''}
                      onChange={e => {
                        setRma(prev => ({ ...prev, customer_phone: e.target.value }))
                      }}
                    />
                  </div>
                  <div className="rma-details-info-grid-item">
                    <div className="rma-details-info-grid-item-label">
                      <Typography variant="body2">Customer Contact Email:</Typography>
                      <Typography variant="body2" color="error">
                        *
                      </Typography>
                    </div>
                    <TextField
                      className="rma-details-info-grid-item-textfield"
                      size="small"
                      fullWidth
                      disabled={!editDetails}
                      value={rma.customer_email ?? ''}
                      onChange={e => {
                        setRma(prev => ({ ...prev, customer_email: e.target.value }))
                      }}
                    />
                  </div>
                </Stack>
              )}
            </Grid>
            <Grid size={4}>
              {loading ? (
                <Stack spacing="24px">
                  <div className="rma-details-info-grid-item">
                    <Skeleton width={150} />
                    <Skeleton variant="rounded" width="100%" height={40} />
                  </div>
                  <div className="rma-details-info-grid-item">
                    <Skeleton width={130} />
                    <Skeleton variant="rounded" width="100%" height={40} />
                  </div>
                </Stack>
              ) : (
                <Stack spacing="24px">
                  <div className="rma-details-info-grid-item">
                    <div className="rma-details-info-grid-item-label">
                      <Typography variant="body2">Pickup Contact Email:</Typography>
                      <Typography variant="body2" color="error">
                        *
                      </Typography>
                    </div>
                    <TextField
                      className="rma-details-info-grid-item-textfield"
                      size="small"
                      fullWidth
                      disabled={!editDetails}
                      value={rma.pickup_email_id ?? ''}
                      onChange={e => {
                        setRma(prev => ({ ...prev, pickup_email_id: e.target.value }))
                      }}
                    />
                  </div>
                  <div className="rma-details-info-grid-item">
                    <div className="rma-details-info-grid-item-label">
                      <Typography variant="body2">Pickup Address:</Typography>
                      <Typography variant="body2" color="error">
                        *
                      </Typography>
                    </div>
                    <Tooltip title={rma.pickup_address} placement="top" arrow>
                      <TextField
                        className="rma-details-info-grid-item-textfield"
                        size="small"
                        fullWidth
                        disabled={!editDetails}
                        value={rma.pickup_address ?? ''}
                        onChange={e => {
                          setRma(prev => ({ ...prev, pickup_address: e.target.value }))
                        }}
                      />
                    </Tooltip>
                  </div>
                </Stack>
              )}
            </Grid>
            <Grid size={4} className="rma-details-info-grid-item">
              {loading ? (
                <>
                  <Skeleton width={180} />
                  <Skeleton variant="rounded" width="100%" height={120} />
                </>
              ) : (
                <>
                  <div className="rma-details-info-grid-item-label">
                    <Typography variant="body2">Any Special Instructions:</Typography>
                  </div>
                  <TextField
                    className="rma-details-info-grid-item-textfield"
                    size="small"
                    fullWidth
                    multiline
                    rows={5.4}
                    disabled={!editDetails}
                    value={rma.special_instructions ?? ''}
                    onChange={e => {
                      setRma(prev => ({ ...prev, special_instructions: e.target.value }))
                    }}
                  />
                </>
              )}
            </Grid>
          </Grid>
        </Container>
        <Accordion
          defaultExpanded
          className="rma-lines-accordion"
          slotProps={{
            transition: {
              onEntered: () => {
                setStickyHeader(true)
              },
              onExit: () => {
                setStickyHeader(false)
              },
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            {loading ? (
              <Skeleton width={150} height={32} />
            ) : (
              <div className="rma-lines-accordion-header">
                <Typography variant="h6">RMA Line Items</Typography>
                {id !== 'create' && (user.user_type === 'Operation Staff' || user.user_type === 'Administrator') && (
                  <div className="rma-lines-edit-buttons">
                    {editRmaLines ? (
                      <>
                        <Button
                          onClick={e => {
                            e.stopPropagation()
                            handleCancelRmaLines()
                          }}
                          loading={loading}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<SaveOutlined />}
                          onClick={e => {
                            e.stopPropagation()
                            handleSaveRmaLines()
                          }}
                          loading={loading}
                        >
                          Save Changes
                        </Button>
                      </>
                    ) : (
                      <Button
                        startIcon={<EditOutlined />}
                        onClick={e => {
                          e.stopPropagation()
                          setEditRmaLines(true)
                        }}
                        loading={loading}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </AccordionSummary>
          <AccordionDetails className="rma-lines">
            {id === 'create' && (
              <>
                <Divider />
                <div className="rma-lines-accordion-create">
                  <Typography color="text.secondary">{selectedLines.length} Selected</Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      if (selectedLines.length === rmaLines.length) {
                        setDeleteRmaDialogOpen(true)
                      } else {
                        setRmaLines(prev => prev.filter(line => !selectedLines.includes(line)))
                        setSelectedLines([])
                      }
                    }}
                    disabled={selectedLines.length === 0}
                    loading={loading}
                  >
                    Remove From RMA
                  </Button>
                </div>
                <Dialog open={deleteRmaDialogOpen} maxWidth="md" onClose={() => setDeleteRmaDialogOpen(false)}>
                  <DialogTitle>Line Item Required to Create RMA</DialogTitle>
                  <DialogContent>
                    Deleting {selectedLines.length === 1 ? 'this single item' : 'these line items'} will prevent you
                    from creating an RMA. Would you like to cancel this RMA draft?
                  </DialogContent>
                  <DialogActions>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setDeleteRmaDialogOpen(false)
                        setSelectedLines([])
                      }}
                    >
                      No, continue RMA draft
                    </Button>
                    <Button variant="contained" onClick={() => navigate(`/salesorder/${rma.sales_order_txn_id}`)}>
                      Yes, cancel RMA draft
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )}
            <TableContainer>
              <Table stickyHeader={stickyHeader} color="primary">
                <TableHead>
                  {loading ? (
                    <TableRow>
                      {id === 'create' && (
                        <TableCell width="42px">
                          <Skeleton width={24} height={24} />
                        </TableCell>
                      )}
                      {Array.from({ length: 5 }).map((_, index) => (
                        <TableCell key={index}>
                          <Skeleton className="skeleton-header" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ) : (
                    <TableRow>
                      {id === 'create' && (
                        <TableCell width="42px">
                          <Checkbox
                            checked={selectedLines.length === rmaLines.length}
                            onChange={(_, checked) => {
                              if (checked) {
                                setSelectedLines(rmaLines)
                              } else {
                                setSelectedLines([])
                              }
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <TableSortLabel>Item Part No</TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel>Item Description</TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel>RMA Qty</TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel>Serial Number</TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel>Comments</TableSortLabel>
                      </TableCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {loading
                    ? Array.from({ length: 5 }).map((_, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {id === 'create' && (
                            <TableCell>
                              <Skeleton width={24} height={24} />
                            </TableCell>
                          )}
                          {Array.from({ length: 5 }).map((_, colIndex) => (
                            <TableCell key={colIndex}>
                              <Skeleton />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : rmaLines.map(line => (
                        <TableRow key={line.sales_order_line_txn_id}>
                          {id === 'create' && (
                            <TableCell>
                              <Checkbox
                                checked={selectedLines.includes(line)}
                                onChange={(_, checked) => {
                                  if (checked) {
                                    setSelectedLines(prev => [...prev, line])
                                  } else {
                                    setSelectedLines(prev =>
                                      prev.filter(l => l.sales_order_line_txn_id !== line.sales_order_line_txn_id),
                                    )
                                  }
                                }}
                              />
                            </TableCell>
                          )}
                          <TableCell>{line.item_name}</TableCell>
                          <TableCell>
                            <Expandable text={line.item_desc} />
                          </TableCell>
                          <TableCell align="right">
                            {id === 'create' ? (
                              <NumberField
                                label=""
                                size="small"
                                value={line.rma_quantity}
                                onChange={e => {
                                  setRmaLines(prev =>
                                    prev.map(l =>
                                      l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                        ? { ...l, rma_quantity: e?.valueOf() ?? 0 }
                                        : l,
                                    ),
                                  )
                                }}
                              />
                            ) : editRmaLines ? (
                              <NumberField
                                label=""
                                size="small"
                                fullWidth
                                className="rma-lines-edit-right-align"
                                value={line.rma_quantity}
                                onChange={e => {
                                  setRmaLines(prev =>
                                    prev.map(l =>
                                      l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                        ? { ...l, rma_quantity: e?.valueOf() ?? 0 }
                                        : l,
                                    ),
                                  )
                                }}
                              />
                            ) : line.rma_quantity > 0 ? (
                              line.rma_quantity
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {id === 'create' ? (
                              <TextField
                                size="small"
                                fullWidth
                                value={line.serial_number ?? ''}
                                onChange={e => {
                                  setRmaLines(prev =>
                                    prev.map(l =>
                                      l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                        ? { ...l, serial_number: e.target.value || null }
                                        : l,
                                    ),
                                  )
                                }}
                              />
                            ) : editRmaLines ? (
                              <TextField
                                className="rma-lines-edit-right-align"
                                size="small"
                                fullWidth
                                value={line.serial_number ?? ''}
                                onChange={e => {
                                  setRmaLines(prev =>
                                    prev.map(l =>
                                      l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                        ? { ...l, serial_number: e.target.value || null }
                                        : l,
                                    ),
                                  )
                                }}
                              />
                            ) : (
                              line.serial_number ?? '-'
                            )}
                          </TableCell>
                          <TableCell>
                            {id === 'create' ? (
                              <TextField
                                size="small"
                                fullWidth
                                value={line.rma_comment ?? ''}
                                onChange={e => {
                                  setRmaLines(prev =>
                                    prev.map(l =>
                                      l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                        ? { ...l, rma_comment: e.target.value || null }
                                        : l,
                                    ),
                                  )
                                }}
                              />
                            ) : editRmaLines ? (
                              <TextField
                                size="small"
                                fullWidth
                                value={line.rma_comment ?? ''}
                                onChange={e => {
                                  setRmaLines(prev =>
                                    prev.map(l =>
                                      l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                        ? { ...l, rma_comment: e.target.value || null }
                                        : l,
                                    ),
                                  )
                                }}
                              />
                            ) : (
                              line.rma_comment
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
        {id !== 'create' && (
          <>
            <div className="rma-details-emails">
              <div className="rma-details-emails-tabs">
                {loading ? (
                  <div className="rma-details-emails-loading">
                    <Skeleton width={120} height={48} />
                    <Skeleton width={180} height={48} />
                    <Skeleton width={140} height={48} />
                  </div>
                ) : (
                  <Tabs value={selectedEmail} onChange={(_, value) => setSelectedEmail(value)}>
                    <Tab label="Email to Vendor" />
                    <Tab label="Internal Escalation Email" />
                    <Tab label="Email to Customer" />
                  </Tabs>
                )}
                {user.user_type !== 'Office Staff' &&
                  user.user_type !== 'Sales Rep' &&
                  (editEmail ? (
                    <div className="rma-details-emails-save-button-container">
                      <Button
                        onClick={() => {
                          setEditEmail(false)
                          setRma(rmaCopy)
                        }}
                        loading={loading}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<SaveOutlined />}
                        onClick={handleSaveEmail}
                        loading={loading}
                      >
                        Save Changes
                      </Button>
                    </div>
                  ) : (
                    <Button startIcon={<EditOutlined />} onClick={() => setEditEmail(true)} loading={loading}>
                      Edit
                    </Button>
                  ))}
              </div>
              <Divider />
              {selectedEmail === 0 && (
                <RmaEmailToVendor
                  key={rma.rma_id}
                  rma={rma}
                  rmaLines={rmaLines}
                  edit={editEmail}
                  userType={user.user_type}
                  setRma={setRma}
                  loading={loading}
                />
              )}
              {selectedEmail === 1 && (
                <RmaInternalEscalationEmail
                  key={rma.rma_id}
                  rma={rma}
                  rmaLines={rmaLines}
                  edit={editEmail}
                  userType={user.user_type}
                  setRma={setRma}
                  loading={loading}
                />
              )}
              {selectedEmail === 2 && (
                <RmaEmailToCustomer
                  key={rma.rma_id}
                  rma={rma}
                  rmaLines={rmaLines}
                  edit={editEmail}
                  userType={user.user_type}
                  setRma={setRma}
                  loading={loading}
                />
              )}
            </div>
            <TableContainer className="rma-related-invoices">
              {loading ? (
                <Skeleton width={150} height={32} />
              ) : (
                <Typography variant="h6" className="rma-related-invoices-header">
                  Related Invoices
                </Typography>
              )}
              <Table color="primary">
                <TableHead>
                  {loading ? (
                    <TableRow>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <TableCell key={index}>
                          <Skeleton className="skeleton-header" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell>
                        <TableSortLabel>Reference Number</TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel>Transaction Date</TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel>Due Date</TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel>Amount</TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel>Status</TableSortLabel>
                      </TableCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {loading
                    ? Array.from({ length: 3 }).map((_, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {Array.from({ length: 5 }).map((_, colIndex) => (
                            <TableCell key={colIndex}>
                              <Skeleton />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : relatedInvoices.map(item => (
                        <TableRow key={item.invoice_txn_id}>
                          <TableCell>
                            <LinkMui to={`/customer/invoice/${item.invoice_txn_id}`} component={LinkRouter}>
                              {item.ref_number}
                            </LinkMui>
                          </TableCell>
                          <TableCell align="right">{dayjs(item.txn_date).format('MM/DD/YYYY')}</TableCell>
                          <TableCell align="right">{dayjs(item.due_date).format('MM/DD/YYYY')}</TableCell>
                          <TableCell align="right">
                            ${((item.applied_amount ?? 0) * -1 + (item.balance_amount ?? 0)).toFixed(2)}
                          </TableCell>
                          <TableCell>{item.is_paid ? 'Paid' : 'Not Paid'}</TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </div>
    </div>
  )
}

export default RmaDetails
